// California State University, Fullerton: public college page (lane 2, #2745),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - CSUF's Common Data Set 2025-2026 (Institutional Research, Excel file):
//    C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8A (no use of SAT or ACT), C21/C22 (no early decision or early
//    action), G1/G5 2026-2027 costs.
//  - CSUF Office of Admissions: Admission Deadlines (Fall 2027 Undergraduate
//    First-Time Freshmen) and First Time First Year Admission Requirements
//    (the CSU's end of the SAT and ACT, campus and major impaction, the
//    local admission area, majors with supplemental requirements). The
//    requirements page loads its sections in the browser; it was read in a
//    headless browser.
//  - College Scorecard API, school id 110565 (net price by family income).
// The CDS residency table also has 117 applicants of unknown residency, not
// shown as their own row. The CDS out-of-state tuition ($18,142) is the full
// tuition a nonresident pays, as the CDS lists it.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'CSUF Common Data Set 2025-2026',
        url: 'https://www.fullerton.edu/data/_resources/pdfs/institutional-research/cds/CDS-2025-2026.xlsx',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'CSUF Admissions, Admission Deadlines',
        url: 'https://admissions.fullerton.edu/deadlines/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'CSUF Admissions, First Time First Year Admission Requirements',
        url: 'https://admissions.fullerton.edu/prospective-students/first-year.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110565-California-State-University-Fullerton',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'california-state-university-fullerton',
    kbId: 'california_state_university_fullerton',
    name: 'California State University, Fullerton',
    shortName: 'Cal State Fullerton',
    aliases: ['CSUF', 'Fullerton'],
    location: 'Fullerton, CA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (CSUF brand colors); decoration only.
    branding: {
        primary: '#00244E',
        secondary: '#FF7900',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/california_state_university_fullerton_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 55289,
        admitted: 48716,
        enrolled: 6787,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'California residents', applied: 52429, admitted: 46493 },
            { group: 'Out-of-state', applied: 2316, admitted: 1938 },
            { group: 'International', applied: 427, admitted: 169 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (117 applicants of unknown residency not shown)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'CSUF\'s Common Data Set reports one first-year acceptance rate for the whole university. CSUF is impacted by campus and by major, but it publishes no admission rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'CSUF has no early decision or early action plan; the Common Data Set answers no to both. There is one Cal State Apply filing period.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'CSUF is impacted by campus and by major: there are more qualified applicants than seats, and admission uses a Multi-Factor Admission Score. Applicants from the local area (all of Orange County\'s high schools plus Chino, Corona/Norco, Walnut, Whittier and the Alvord district) are admitted at a lower score. These majors ask for more:',
        rows: [
            { school: 'Computer Engineering integrated BS/MS', requirement: 'A minimum unweighted high school GPA of 3.0.' },
            { school: 'Dance, Music, Theatre', requirement: 'Admission to the major is based on a successful live audition.' },
            { school: 'Nursing', requirement: 'Pathway requirements and application steps set by the School of Nursing.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'Cal State Apply', deadline: 'November 30', decision: null, binding: false },
        ],
        note: 'You apply through Cal State Apply. The application opens August 1, 2026, and you can submit from October 1 to November 30, 2026. Once you submit, your major cannot be changed until the first day of the semester. If admitted, the intent to enroll deadline is May 1, 2027. CSUF does not publish decision dates.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'SAT and ACT scores are not used: the California State University has discontinued the use of ACT and SAT examinations in determining admission eligibility.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8A',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [6838, 18142] },
            { item: 'Required fees', amounts: [1678, 1678] },
            { item: 'Food and housing (on campus)', amounts: [20384, 20384] },
            { item: 'Books and supplies', amounts: [1078, 1078] },
            { item: 'Transportation', amounts: [1678, 1678] },
            { item: 'Other expenses', amounts: [3274, 3274] },
        ],
        totals: [34930, 46234],
        note: 'For a full-time undergraduate living on campus, fall and spring. The out-of-state tuition is the full tuition a nonresident pays.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 5115,
            byIncome: [
                { income: '$0 to $30,000', amount: 2620 },
                { income: '$30,001 to $48,000', amount: 2878 },
                { income: '$48,001 to $75,000', amount: 3852 },
                { income: '$75,001 to $110,000', amount: 6994 },
                { income: 'Over $110,000', amount: 14753 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from CSUF\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
